import React, { useState, useEffect } from 'react';
import { useToast } from '../../../components/Toast';
import { EDUCATION_DEFAULTS } from '../../../data/educationDefaults';
import './EducationAssessmentSystemForm.css';

export default function EducationAssessmentSystemForm({ apiBase }) {
  const toastUtil = useToast();
  const ToastContainer = toastUtil.ToastContainer;
  const toast = toastUtil;

  const defaults = EDUCATION_DEFAULTS.assessment_system;
  const defaultsMeaning = EDUCATION_DEFAULTS.what_this_means || [];
  const defaultsCalendar = EDUCATION_DEFAULTS.assessment_calendar || [];

  const [formData, setFormData] = useState({
    title: defaults.title,
    summary: defaults.summary,
    balanced_approach_title: defaults.balanced_approach_title,
    balanced_approach_description: defaults.balanced_approach_description,
    assessment_methodology_title: defaults.assessment_methodology_title,
    assessment_methodology_subtitle: defaults.assessment_methodology_subtitle,
    assessment_calendar_header_title: defaults.assessment_calendar_header_title,
    assessment_calendar_header_subtitle: defaults.assessment_calendar_header_subtitle,
    points: [...defaults.points],
    methodology: [...defaults.methodology],
    what_this_means: [...defaultsMeaning],
    assessment_calendar: [...defaultsCalendar]
  });

  const [originalData, setOriginalData] = useState({ ...formData });
  const [pendingFields, setPendingFields] = useState({});
  const [loading, setLoading] = useState(true);
  const [sectionId, setSectionId] = useState(null);
  const [submitting, setSubmitting] = useState({});
  const [activeTab, setActiveTab] = useState('overview-header');
  const [editingPointIndex, setEditingPointIndex] = useState(null);
  const [pointModalData, setPointModalData] = useState(null);
  const [editingMethodIndex, setEditingMethodIndex] = useState(null);
  const [methodModalData, setMethodModalData] = useState(null);
  const [editingMeaningIndex, setEditingMeaningIndex] = useState(null);
  const [meaningModalData, setMeaningModalData] = useState(null);
  const [editingCalendarIndex, setEditingCalendarIndex] = useState(null);
  const [calendarModalData, setCalendarModalData] = useState(null);
  const [editingBalancedApproach, setEditingBalancedApproach] = useState(false);
  const [balancedApproachData, setBalancedApproachData] = useState(null);
  const [editingMethodologyHeader, setEditingMethodologyHeader] = useState(false);
  const [methodologyHeaderData, setMethodologyHeaderData] = useState(null);
  const [editingCalendarHeader, setEditingCalendarHeader] = useState(false);
  const [calendarHeaderData, setCalendarHeaderData] = useState(null);

  useEffect(() => {
    loadSection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  const loadSection = async () => {
    setLoading(true);
    try {
      let result = null;
      
      // Try authenticated endpoint first
      console.log('[EducationAssessmentSystemForm] Trying authenticated endpoint...');
      const adminResponse = await fetch(
        `${apiBase}/education/cms/sections`,
        { credentials: 'include', headers: { 'Content-Type': 'application/json' } }
      );
      
      if (adminResponse.ok) {
        result = await adminResponse.json();
        console.log('[EducationAssessmentSystemForm] ✓ Got data from admin endpoint');
      } else if (adminResponse.status === 401) {
        console.log('[EducationAssessmentSystemForm] Admin endpoint unauthorized, trying public endpoint...');
        // Fallback to public endpoint
        const publicResponse = await fetch(
          `${apiBase}/education/cms/sections?published=true`,
          { credentials: 'include', headers: { 'Content-Type': 'application/json' } }
        );
        
        if (publicResponse.ok) {
          result = await publicResponse.json();
          console.log('[EducationAssessmentSystemForm] ✓ Got data from public endpoint');
        } else {
          throw new Error(`Public endpoint failed with status ${publicResponse.status}`);
        }
      } else {
        throw new Error(`Admin endpoint failed with status ${adminResponse.status}`);
      }
      
      if (result.ok && Array.isArray(result.data)) {
        console.log('[EducationAssessmentSystemForm] Received sections:', result.data.map(s => s.section_type));
        // Try with -shell suffix first, then without
        let section = result.data.find(s => s.section_type === 'education-assessment-system-shell');
        if (!section) {
          section = result.data.find(s => s.section_type === 'education-assessment-system');
        }
        if (section) {
          setSectionId(section.id);
          console.log('✓ Loaded assessment system section ID:', section.id);
          
          // Check for pending changes
          let pendingFieldsMap = {};
          try {
            const pendingResponse = await fetch(
              `${apiBase}/cms/section/${section.id}/with-pending`,
              { credentials: 'include', headers: { 'Content-Type': 'application/json' } }
            );
            
            if (pendingResponse.ok) {
              const pendingResult = await pendingResponse.json();
              if (pendingResult.success && pendingResult.data && pendingResult.data.pending_changes) {
                const { pending_changes } = pendingResult.data;
                if (Array.isArray(pending_changes)) {
                  pending_changes.forEach(change => {
                    if (change.status === 'pending' && change.locale === 'en') {
                      // Check for point pending
                      if (change.field_id && change.field_id.includes('points.')) {
                        const match = change.field_id.match(/points\.(\d+)\./);
                        if (match) {
                          const pointIdx = parseInt(match[1]);
                          pendingFieldsMap[`point_${pointIdx}`] = true;
                        }
                      }
                      // Check for methodology pending
                      if (change.field_id && change.field_id.includes('methodology.')) {
                        const match = change.field_id.match(/methodology\.(\d+)\./);
                        if (match) {
                          const methodIdx = parseInt(match[1]);
                          pendingFieldsMap[`method_${methodIdx}`] = true;
                        }
                      }
                      // Check for meaning pending
                      if (change.field_id && change.field_id.includes('what_this_means.')) {
                        const match = change.field_id.match(/what_this_means\.(\d+)\./);
                        if (match) {
                          const meaningIdx = parseInt(match[1]);
                          pendingFieldsMap[`meaning_${meaningIdx}`] = true;
                        }
                      }
                      // Check for calendar pending
                      if (change.field_id && change.field_id.includes('assessment_calendar.')) {
                        const match = change.field_id.match(/assessment_calendar\.(\d+)\./);
                        if (match) {
                          const calendarIdx = parseInt(match[1]);
                          pendingFieldsMap[`calendar_${calendarIdx}`] = true;
                        }
                      }
                    }
                  });
                }
              }
            }
          } catch (err) {
            console.warn('[EducationAssessmentSystemForm] Pending changes check failed:', err.message);
          }
          
          setPendingFields(pendingFieldsMap);
          
          const content = section.content_published || section.content || {};
          const en = content.en || {};
          
          const normalizedData = {
            title: en.title || defaults.title,
            summary: en.summary || defaults.summary,
            balanced_approach_title: en.balanced_approach_title || defaults.balanced_approach_title,
            balanced_approach_description: en.balanced_approach_description || defaults.balanced_approach_description,
            assessment_methodology_title: en.assessment_methodology_title || defaults.assessment_methodology_title,
            assessment_methodology_subtitle: en.assessment_methodology_subtitle || defaults.assessment_methodology_subtitle,
            assessment_calendar_header_title: en.assessment_calendar_header_title || defaults.assessment_calendar_header_title,
            assessment_calendar_header_subtitle: en.assessment_calendar_header_subtitle || defaults.assessment_calendar_header_subtitle,
            points: Array.isArray(en.points) ? en.points : defaults.points,
            methodology: Array.isArray(en.methodology) ? en.methodology : defaults.methodology,
            what_this_means: Array.isArray(en.what_this_means) ? en.what_this_means : defaultsMeaning,
            assessment_calendar: Array.isArray(en.assessment_calendar) ? en.assessment_calendar : defaultsCalendar
          };
          
          setFormData(normalizedData);
          setOriginalData({ ...normalizedData });
        } else {
          console.error('✗ Assessment system section not found in API response');
          console.error('Available section types:', result.data.map(s => s.section_type));
          toast.error('Section not found. Please check with administrator.');
        }
      } else {
        console.error('✗ Invalid API response:', result);
        toast.error('API error loading section');
      }
    } catch (err) {
      console.error('Error loading section:', err.message);
      toast.error('Failed to load section');
    } finally {
      setLoading(false);
    }
  };

  const saveData = async (updateData) => {
    if (!sectionId) {
      toast.error('Section ID not available');
      return false;
    }

    try {
      const payload = {
        content_published: JSON.stringify({
          en: updateData
        })
      };

      const response = await fetch(
        `${apiBase}/education/cms/section/${sectionId}`,
        {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        }
      );

      if (!response.ok) throw new Error('Save failed');

      const result = await response.json();
      return result.success || result.ok;
    } catch (err) {
      console.error('Save error:', err);
      return false;
    }
  };

  const saveOverview = async () => {
    setSubmitting(prev => ({ ...prev, 'overview-header': true }));
    try {
      const success = await saveData(formData);
      if (success) {
        toast.success('Overview saved');
      } else {
        throw new Error('Save failed');
      }
    } catch (err) {
      toast.error('Failed to save overview');
    } finally {
      setSubmitting(prev => ({ ...prev, 'overview-header': false }));
    }
  };

  const editPoint = (index) => {
    setEditingPointIndex(index);
    setPointModalData({...formData.points[index]});
  };

  const savePoint = async () => {
    if (!pointModalData || !pointModalData.title) {
      toast.warning('Please fill in title');
      return;
    }

    const pointKey = `point_${editingPointIndex}`;
    setSubmitting(prev => ({ ...prev, [pointKey]: true }));

    try {
      const response = await fetch(
        `${apiBase}/cms/field-change/submit`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            section_id: sectionId,
            field_id: `points.${editingPointIndex}.update`,
            field_name: `Update Assessment Point ${editingPointIndex + 1}: ${pointModalData.title}`,
            old_value: originalData.points[editingPointIndex] || {},
            new_value: pointModalData,
            locale: 'en'
          })
        }
      );

      const result = await response.json();

      if (result.success) {
        toast.success(`Assessment point ${editingPointIndex + 1} submitted for approval`);
        
        setPendingFields(prev => ({
          ...prev,
          [`point_${editingPointIndex}`]: true
        }));

        const updated = { ...formData };
        updated.points[editingPointIndex] = pointModalData;
        setFormData(updated);
        setOriginalData(prev => ({
          ...prev,
          points: prev.points.map((p, i) => i === editingPointIndex ? pointModalData : p)
        }));

        setEditingPointIndex(null);
        setPointModalData(null);
      } else {
        if (response.status === 409) {
          toast.error(`Assessment point ${editingPointIndex + 1} already has a pending approval`);
        } else {
          toast.error(result.error || result.message || 'Submit failed');
        }
      }
    } catch (err) {
      toast.error('Failed to save');
    } finally {
      setSubmitting(prev => ({ ...prev, [pointKey]: false }));
    }
  };

  const editMethodology = (index) => {
    setEditingMethodIndex(index);
    setMethodModalData({...formData.methodology[index]});
  };

  const saveMethodology = async () => {
    if (!methodModalData || !methodModalData.phase) {
      toast.warning('Please fill in phase name');
      return;
    }

    const methodKey = `method_${editingMethodIndex}`;
    setSubmitting(prev => ({ ...prev, [methodKey]: true }));

    try {
      const response = await fetch(
        `${apiBase}/cms/field-change/submit`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            section_id: sectionId,
            field_id: `methodology.${editingMethodIndex}.update`,
            field_name: `Update Methodology ${editingMethodIndex + 1}: ${methodModalData.phase}`,
            old_value: originalData.methodology[editingMethodIndex] || {},
            new_value: methodModalData,
            locale: 'en'
          })
        }
      );

      const result = await response.json();

      if (result.success) {
        toast.success(`Methodology ${editingMethodIndex + 1} submitted for approval`);
        
        setPendingFields(prev => ({
          ...prev,
          [`method_${editingMethodIndex}`]: true
        }));

        const updated = { ...formData };
        updated.methodology[editingMethodIndex] = methodModalData;
        setFormData(updated);
        setOriginalData(prev => ({
          ...prev,
          methodology: prev.methodology.map((m, i) => i === editingMethodIndex ? methodModalData : m)
        }));

        setEditingMethodIndex(null);
        setMethodModalData(null);
      } else {
        if (response.status === 409) {
          toast.error(`Methodology ${editingMethodIndex + 1} already has a pending approval`);
        } else {
          toast.error(result.error || result.message || 'Submit failed');
        }
      }
    } catch (err) {
      toast.error('Failed to save');
    } finally {
      setSubmitting(prev => ({ ...prev, [methodKey]: false }));
    }
  };

  const editMeaning = (index) => {
    setEditingMeaningIndex(index);
    setMeaningModalData({...formData.what_this_means[index]});
  };

  const saveMeaning = async () => {
    if (!meaningModalData || !meaningModalData.title) {
      toast.warning('Please fill in title');
      return;
    }

    const meaningKey = `meaning_${editingMeaningIndex}`;
    setSubmitting(prev => ({ ...prev, [meaningKey]: true }));

    try {
      const response = await fetch(
        `${apiBase}/cms/field-change/submit`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            section_id: sectionId,
            field_id: `what_this_means.${editingMeaningIndex}.update`,
            field_name: `Update What This Means ${editingMeaningIndex + 1}: ${meaningModalData.title}`,
            old_value: originalData.what_this_means[editingMeaningIndex] || {},
            new_value: meaningModalData,
            locale: 'en'
          })
        }
      );

      const result = await response.json();

      if (result.success) {
        toast.success(`Item ${editingMeaningIndex + 1} submitted for approval`);
        
        setPendingFields(prev => ({
          ...prev,
          [`meaning_${editingMeaningIndex}`]: true
        }));

        const updated = { ...formData };
        updated.what_this_means[editingMeaningIndex] = meaningModalData;
        setFormData(updated);
        setOriginalData(prev => ({
          ...prev,
          what_this_means: prev.what_this_means.map((m, i) => i === editingMeaningIndex ? meaningModalData : m)
        }));

        setEditingMeaningIndex(null);
        setMeaningModalData(null);
      } else {
        if (response.status === 409) {
          toast.error(`Item ${editingMeaningIndex + 1} already has a pending approval`);
        } else {
          toast.error(result.error || result.message || 'Submit failed');
        }
      }
    } catch (err) {
      toast.error('Failed to save');
    } finally {
      setSubmitting(prev => ({ ...prev, [meaningKey]: false }));
    }
  };

  const editCalendar = (index) => {
    setEditingCalendarIndex(index);
    setCalendarModalData({...formData.assessment_calendar[index]});
  };

  const saveCalendar = async () => {
    if (!calendarModalData || !calendarModalData.period) {
      toast.warning('Please fill in period');
      return;
    }

    const calendarKey = `calendar_${editingCalendarIndex}`;
    setSubmitting(prev => ({ ...prev, [calendarKey]: true }));

    try {
      const response = await fetch(
        `${apiBase}/cms/field-change/submit`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            section_id: sectionId,
            field_id: `assessment_calendar.${editingCalendarIndex}.update`,
            field_name: `Update Assessment Calendar ${editingCalendarIndex + 1}: ${calendarModalData.period}`,
            old_value: originalData.assessment_calendar[editingCalendarIndex] || {},
            new_value: calendarModalData,
            locale: 'en'
          })
        }
      );

      const result = await response.json();

      if (result.success) {
        toast.success(`Calendar item ${editingCalendarIndex + 1} submitted for approval`);
        
        setPendingFields(prev => ({
          ...prev,
          [`calendar_${editingCalendarIndex}`]: true
        }));

        const updated = { ...formData };
        updated.assessment_calendar[editingCalendarIndex] = calendarModalData;
        setFormData(updated);
        setOriginalData(prev => ({
          ...prev,
          assessment_calendar: prev.assessment_calendar.map((c, i) => i === editingCalendarIndex ? calendarModalData : c)
        }));

        setEditingCalendarIndex(null);
        setCalendarModalData(null);
      } else {
        if (response.status === 409) {
          toast.error(`Calendar item ${editingCalendarIndex + 1} already has a pending approval`);
        } else {
          toast.error(result.error || result.message || 'Submit failed');
        }
      }
    } catch (err) {
      toast.error('Failed to save');
    } finally {
      setSubmitting(prev => ({ ...prev, [calendarKey]: false }));
    }
  };

  if (loading) {
    return (
      <div className="education-assessment-form-container">
        <div className="hero-form-wrapper">
          <div className="loading">
            <div className="loading-spinner"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="education-assessment-form-container">
      <ToastContainer />

      <div className="hero-form-wrapper">
        <div className="hero-form-header">
          <h2 className="hero-form-title">{formData.title}</h2>
          <p className="hero-form-description">{formData.summary}</p>
        </div>

        <div className="tabs-wrapper">
          <div className="tabs-nav">
            <button
              className={`tab-button ${activeTab === 'overview-header' ? 'active' : ''}`}
              onClick={() => setActiveTab('overview-header')}
            >
              Overview
            </button>
            <button
              className={`tab-button ${activeTab === 'balanced-approach' ? 'active' : ''}`}
              onClick={() => setActiveTab('balanced-approach')}
            >
              Our Balanced Approach
            </button>
            <button
              className={`tab-button ${activeTab === 'methodology' ? 'active' : ''}`}
              onClick={() => setActiveTab('methodology')}
            >
              Our Assessment Methodology
            </button>
            <button
              className={`tab-button ${activeTab === 'meaning' ? 'active' : ''}`}
              onClick={() => setActiveTab('meaning')}
            >
              What This Means for Your Child
            </button>
            <button
              className={`tab-button ${activeTab === 'calendar' ? 'active' : ''}`}
              onClick={() => setActiveTab('calendar')}
            >
              Assessment Calendar
            </button>
          </div>
        </div>

        {activeTab === 'overview-header' && (
          <div className="form-section">
            <h3 className="form-section-title">Section Overview</h3>
            <div className="form-field-card">
              <label>Title</label>
              <input type="text" className="text-input" value={formData.title} onChange={(e) => setFormData(prev => ({...prev, title: e.target.value}))} maxLength="100" />
            </div>
            <div className="form-field-card">
              <label>Summary</label>
              <textarea className="textarea-input" value={formData.summary} onChange={(e) => setFormData(prev => ({...prev, summary: e.target.value}))} rows={3} maxLength="500" />
            </div>
            <button className="submit-btn" onClick={saveOverview} disabled={submitting['overview-header']}>
              {submitting['overview-header'] ? 'Saving...' : 'Save Overview'}
            </button>
          </div>
        )}

        {activeTab === 'balanced-approach' && (
          <div className="form-section">
            <h3 className="form-section-title">Our Balanced Approach</h3>
            <div className="form-field-card">
              <label>Title</label>
              <input type="text" className="text-input" value={formData.balanced_approach_title} onChange={(e) => setFormData(prev => ({...prev, balanced_approach_title: e.target.value}))} maxLength="100" />
            </div>
            <div className="form-field-card">
              <label>Description</label>
              <textarea className="textarea-input" value={formData.balanced_approach_description} onChange={(e) => setFormData(prev => ({...prev, balanced_approach_description: e.target.value}))} rows={4} maxLength="500" />
            </div>
            <button className="submit-btn" onClick={saveOverview} disabled={submitting['overview-header']} style={{marginBottom: '24px'}}>
              {submitting['overview-header'] ? 'Saving...' : 'Save Balanced Approach'}
            </button>

            <h4 style={{marginTop: '24px', marginBottom: '12px', fontSize: '16px', fontWeight: '600'}}>Assessment Points</h4>
            <div className="level-cards-grid">
              {Array.isArray(formData.points) && formData.points.map((point, idx) => (
                <button
                  key={idx}
                  type="button"
                  className="level-card"
                  onClick={() => editPoint(idx)}
                >
                  <div className="level-card-content">
                    <div className="level-card-header">
                      <h4 className="level-card-title">{point.title}</h4>
                      {pendingFields[`point_${idx}`] && (
                        <span className="level-card-badge">⏳</span>
                      )}
                    </div>
                    <p className="level-card-desc">{point.description.substring(0, 80)}...</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'methodology' && (
          <div className="form-section">
            <h3 className="form-section-title">Our Assessment Methodology</h3>
            <div className="form-field-card">
              <label>Header Title</label>
              <input type="text" className="text-input" value={formData.assessment_methodology_title} onChange={(e) => setFormData(prev => ({...prev, assessment_methodology_title: e.target.value}))} maxLength="100" />
            </div>
            <div className="form-field-card">
              <label>Header Subtitle</label>
              <input type="text" className="text-input" value={formData.assessment_methodology_subtitle} onChange={(e) => setFormData(prev => ({...prev, assessment_methodology_subtitle: e.target.value}))} maxLength="150" />
            </div>
            <button className="submit-btn" onClick={saveOverview} disabled={submitting['overview-header']} style={{marginBottom: '24px'}}>
              {submitting['overview-header'] ? 'Saving...' : 'Save Methodology Header'}
            </button>

            <h4 style={{marginTop: '24px', marginBottom: '12px', fontSize: '16px', fontWeight: '600'}}>Methodology Pillars</h4>
            {Array.isArray(formData.methodology) && formData.methodology.map((phase, idx) => (
              <button
                key={idx}
                type="button"
                className="level-card"
                onClick={() => editMethodology(idx)}
                style={{marginBottom: '16px', textAlign: 'left'}}
              >
                <div className="level-card-content">
                  <div className="level-card-header">
                    <h4 className="level-card-title">{phase.phase}</h4>
                    {pendingFields[`method_${idx}`] && (
                      <span className="level-card-badge">⏳</span>
                    )}
                  </div>
                  <p className="level-card-desc">{phase.description}</p>
                </div>
              </button>
            ))}
          </div>
        )}

        {activeTab === 'meaning' && (
          <div className="form-section">
            <h3 className="form-section-title">What This Means for Your Child</h3>
            
            <div className="level-cards-grid">
              {Array.isArray(formData.what_this_means) && formData.what_this_means.map((item, idx) => (
                <button
                  key={idx}
                  type="button"
                  className="level-card"
                  onClick={() => editMeaning(idx)}
                >
                  <div className="level-card-content">
                    <div className="level-card-header">
                      <h4 className="level-card-title">{item.title}</h4>
                      {pendingFields[`meaning_${idx}`] && (
                        <span className="level-card-badge">⏳</span>
                      )}
                    </div>
                    <p className="level-card-desc">{item.description}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'calendar' && (
          <div className="form-section">
            <h3 className="form-section-title">Assessment Calendar</h3>
            <div className="form-field-card">
              <label>Header Title</label>
              <input type="text" className="text-input" value={formData.assessment_calendar_header_title} onChange={(e) => setFormData(prev => ({...prev, assessment_calendar_header_title: e.target.value}))} maxLength="100" />
            </div>
            <div className="form-field-card">
              <label>Header Subtitle</label>
              <input type="text" className="text-input" value={formData.assessment_calendar_header_subtitle} onChange={(e) => setFormData(prev => ({...prev, assessment_calendar_header_subtitle: e.target.value}))} maxLength="150" />
            </div>
            <button className="submit-btn" onClick={saveOverview} disabled={submitting['overview-header']} style={{marginBottom: '24px'}}>
              {submitting['overview-header'] ? 'Saving...' : 'Save Calendar Header'}
            </button>

            <h4 style={{marginTop: '24px', marginBottom: '12px', fontSize: '16px', fontWeight: '600'}}>Calendar Periods</h4>
            {Array.isArray(formData.assessment_calendar) && formData.assessment_calendar.map((item, idx) => {
              const today = new Date();
              const startDate = new Date(item.date_start);
              const endDate = new Date(item.date_end);
              const statusBadge = today < startDate ? 'upcoming' : today > endDate ? 'completed' : 'active';
              
              return (
              <button
                key={idx}
                type="button"
                className="level-card"
                onClick={() => editCalendar(idx)}
                style={{marginBottom: '16px', textAlign: 'left'}}
              >
                <div className="level-card-content">
                  <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px'}}>
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', flex: 1}}>
                      <h4 className="level-card-title">{item.period}</h4>
                      {pendingFields[`calendar_${idx}`] && (
                        <span className="level-card-badge">⏳</span>
                      )}
                    </div>
                    <span style={{fontSize: '11px', fontWeight: 700, padding: '4px 12px', borderRadius: '20px', background: statusBadge === 'active' ? '#c6f6d5' : statusBadge === 'completed' ? '#e2e8f0' : '#bee3f8', color: statusBadge === 'active' ? '#22543d' : statusBadge === 'completed' ? '#2d3748' : '#2c5282', textTransform: 'uppercase', letterSpacing: '0.3px'}}>{statusBadge}</span>
                  </div>
                  <p style={{fontSize: '12px', color: '#a0aec0', margin: '4px 0'}}>{item.date_start} → {item.date_end}</p>
                  <ul style={{margin: '8px 0 0 0', paddingLeft: '20px', fontSize: '13px', color: '#718096'}}>
                    {Array.isArray(item.activities) && item.activities.map((act, aIdx) => (
                      <li key={aIdx} style={{marginBottom: '4px'}}>{act.substring(0, 50)}...</li>
                    ))}
                  </ul>
                </div>
              </button>
            );
            })}
          </div>
        )}
      </div>

      {/* POINT EDIT MODAL */}
      {editingPointIndex !== null && pointModalData && (
        <div className="modal-overlay" onClick={() => {
          setEditingPointIndex(null);
          setPointModalData(null);
        }}>
          <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit Assessment Point</h3>
              <button className="modal-close" onClick={() => {
                setEditingPointIndex(null);
                setPointModalData(null);
              }}>×</button>
            </div>

            <div className="modal-body">
              <div className="modal-field">
                <label>Title</label>
                <input
                  type="text"
                  className="modal-input"
                  value={pointModalData.title}
                  onChange={(e) => setPointModalData(prev => ({...prev, title: e.target.value}))}
                  maxLength="100"
                />
              </div>

              <div className="modal-field">
                <label>Description</label>
                <textarea
                  className="modal-textarea"
                  value={pointModalData.description}
                  onChange={(e) => setPointModalData(prev => ({...prev, description: e.target.value}))}
                  rows={3}
                  maxLength="300"
                />
              </div>

              <div className="modal-field">
                <label>Details</label>
                {Array.isArray(pointModalData.details) && pointModalData.details.map((detail, idx) => (
                  <textarea
                    key={idx}
                    className="modal-textarea"
                    value={detail}
                    onChange={(e) => {
                      const updated = [...pointModalData.details];
                      updated[idx] = e.target.value;
                      setPointModalData(prev => ({...prev, details: updated}));
                    }}
                    placeholder="Detail..."
                    rows={2}
                    style={{marginBottom: '8px'}}
                  />
                ))}
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => {
                setEditingPointIndex(null);
                setPointModalData(null);
              }}>Cancel</button>
              <button
                className="btn-submit"
                onClick={savePoint}
                disabled={submitting[`point_${editingPointIndex}`] || pendingFields[`point_${editingPointIndex}`]}
              >
                {submitting[`point_${editingPointIndex}`] ? 'Saving...' : pendingFields[`point_${editingPointIndex}`] ? 'Pending Approval' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* METHODOLOGY EDIT MODAL */}
      {editingMethodIndex !== null && methodModalData && (
        <div className="modal-overlay" onClick={() => {
          setEditingMethodIndex(null);
          setMethodModalData(null);
        }}>
          <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit Methodology Phase</h3>
              <button className="modal-close" onClick={() => {
                setEditingMethodIndex(null);
                setMethodModalData(null);
              }}>×</button>
            </div>

            <div className="modal-body">
              <div className="modal-field">
                <label>Phase Name</label>
                <input
                  type="text"
                  className="modal-input"
                  value={methodModalData.phase}
                  onChange={(e) => setMethodModalData(prev => ({...prev, phase: e.target.value}))}
                  maxLength="100"
                />
              </div>

              <div className="modal-field">
                <label>Description</label>
                <textarea
                  className="modal-textarea"
                  value={methodModalData.description}
                  onChange={(e) => setMethodModalData(prev => ({...prev, description: e.target.value}))}
                  rows={4}
                  maxLength="300"
                />
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => {
                setEditingMethodIndex(null);
                setMethodModalData(null);
              }}>Cancel</button>
              <button
                className="btn-submit"
                onClick={saveMethodology}
                disabled={submitting[`method_${editingMethodIndex}`] || pendingFields[`method_${editingMethodIndex}`]}
              >
                {submitting[`method_${editingMethodIndex}`] ? 'Saving...' : pendingFields[`method_${editingMethodIndex}`] ? 'Pending Approval' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MEANING EDIT MODAL */}
      {editingMeaningIndex !== null && meaningModalData && (
        <div className="modal-overlay" onClick={() => {
          setEditingMeaningIndex(null);
          setMeaningModalData(null);
        }}>
          <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit Meaning for Child</h3>
              <button className="modal-close" onClick={() => {
                setEditingMeaningIndex(null);
                setMeaningModalData(null);
              }}>×</button>
            </div>

            <div className="modal-body">
              <div className="modal-field">
                <label>Title</label>
                <input
                  type="text"
                  className="modal-input"
                  value={meaningModalData.title}
                  onChange={(e) => setMeaningModalData(prev => ({...prev, title: e.target.value}))}
                  maxLength="100"
                />
              </div>

              <div className="modal-field">
                <label>Description</label>
                <textarea
                  className="modal-textarea"
                  value={meaningModalData.description}
                  onChange={(e) => setMeaningModalData(prev => ({...prev, description: e.target.value}))}
                  rows={4}
                  maxLength="300"
                />
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => {
                setEditingMeaningIndex(null);
                setMeaningModalData(null);
              }}>Cancel</button>
              <button
                className="btn-submit"
                onClick={saveMeaning}
                disabled={submitting[`meaning_${editingMeaningIndex}`] || pendingFields[`meaning_${editingMeaningIndex}`]}
              >
                {submitting[`meaning_${editingMeaningIndex}`] ? 'Saving...' : pendingFields[`meaning_${editingMeaningIndex}`] ? 'Pending Approval' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CALENDAR EDIT MODAL */}
      {editingCalendarIndex !== null && calendarModalData && (
        <div className="modal-overlay" onClick={() => {
          setEditingCalendarIndex(null);
          setCalendarModalData(null);
        }}>
          <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit Calendar Period</h3>
              <button className="modal-close" onClick={() => {
                setEditingCalendarIndex(null);
                setCalendarModalData(null);
              }}>×</button>
            </div>

            <div className="modal-body">
              <div className="modal-field">
                <label>Period</label>
                <input
                  type="text"
                  className="modal-input"
                  value={calendarModalData.period}
                  onChange={(e) => setCalendarModalData(prev => ({...prev, period: e.target.value}))}
                  maxLength="100"
                />
              </div>

              <div className="modal-field">
                <label>Activities</label>
                {Array.isArray(calendarModalData.activities) && calendarModalData.activities.map((activity, idx) => (
                  <textarea
                    key={idx}
                    className="modal-textarea"
                    value={activity}
                    onChange={(e) => {
                      const updated = [...calendarModalData.activities];
                      updated[idx] = e.target.value;
                      setCalendarModalData(prev => ({...prev, activities: updated}));
                    }}
                    placeholder="Activity..."
                    rows={2}
                    style={{marginBottom: '8px'}}
                  />
                ))}
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => {
                setEditingCalendarIndex(null);
                setCalendarModalData(null);
              }}>Cancel</button>
              <button
                className="btn-submit"
                onClick={saveCalendar}
                disabled={submitting[`calendar_${editingCalendarIndex}`] || pendingFields[`calendar_${editingCalendarIndex}`]}
              >
                {submitting[`calendar_${editingCalendarIndex}`] ? 'Saving...' : pendingFields[`calendar_${editingCalendarIndex}`] ? 'Pending Approval' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
